import { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Loader2, Plus, X, CheckCircle2, Upload, FileText, Wand2 } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { taskApi, documentApi } from '@/services/api';
import type { TemplateItem, FieldValue } from '@/components/TemplatesPage';

type WizardStep = 'intro' | 'upload' | 'processing' | 'review-fields' | 'response-type' | 'done';

const STEPS: WizardStep[] = ['intro', 'upload', 'processing', 'review-fields', 'response-type', 'done'];

const STEP_TITLES: Record<WizardStep, string> = {
  intro: 'How Templates Work',
  upload: 'Upload Your Template',
  processing: 'Analyzing Your Template…',
  'review-fields': 'Review Template Fields',
  'response-type': 'Categorize Your Template',
  done: 'Template Ready!',
};

const RESPONSE_TYPE_OPTIONS = [
  { value: 'non-final', label: 'Non-Final OA Response', description: 'For responding to non-final office actions' },
  { value: 'final', label: 'Final OA Response', description: 'For responding to final office actions' },
  { value: 'restriction-election', label: 'Restriction/Election Response', description: 'For restriction or election requirements' },
  { value: 'preliminary-amendment', label: 'Preliminary Amendment', description: 'For amendments filed before first action' },
  { value: 'general-transmittal', label: 'General Transmittal', description: 'For general transmittal letters' },
  { value: 'ex-parte-quayle', label: 'Response to Ex Parte Quayle', description: 'For Ex Parte Quayle actions' },
  { value: 'notice-of-allowance', label: 'Comments on Notice of Allowance', description: 'For responses after allowance' },
  { value: 'correct-application-papers', label: 'Response to Notice of Correct Application Papers', description: 'For correcting application papers' },
] as const;

// --- Field helpers (same as TemplateDocxViewer.tsx) ---

function stripBraces(key: string): string {
  return key.replace(/^\{\{\s*/, '').replace(/\s*\}\}$/, '');
}

function wrapBraces(key: string): string {
  const stripped = stripBraces(key);
  return stripped ? `{{${stripped}}}` : '';
}

function entriesToRecord(entries: [string, FieldValue][]): Record<string, FieldValue> {
  const record: Record<string, FieldValue> = {};
  for (const [key, value] of entries) {
    const wrapped = wrapBraces(key);
    if (wrapped) record[wrapped] = value;
  }
  return record;
}

function normalizeFieldValue(value: string | FieldValue | unknown): FieldValue {
  if (typeof value === 'object' && value !== null && 'description' in (value as object)) {
    const obj = value as FieldValue;
    return { description: obj.description || '', example: obj.example || '' };
  }
  if (typeof value === 'string') {
    return { description: value, example: '' };
  }
  return { description: '', example: '' };
}

// --- Step sub-components ---

function IntroStep() {
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">
        Templates are <strong>.docx</strong> files that define your firm's standard USPTO response format.
        Upload your firm's letterhead template and PatentLoop AI will automatically fill in the case-specific details.
      </p>

      <div className="space-y-3">
        <p className="text-sm font-medium text-slate-700">Your template can contain two types of placeholders:</p>

        <div className="rounded-lg border border-teal-100 bg-teal-50 p-3 space-y-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-teal-700 uppercase tracking-wide">Dynamic Fields</span>
            <span className="text-xs text-teal-600">— short values auto-filled from case data</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {['serial_number', 'examiner_name', 'office_action_date', 'art_unit', 'attorney_docket'].map((f) => (
              <span key={f} className="inline-flex items-center px-2 py-0.5 rounded bg-teal-100 text-teal-800 text-xs font-mono">
                {`{{${f}}}`}
              </span>
            ))}
          </div>
        </div>

        <div className="rounded-lg border border-purple-100 bg-purple-50 p-3 space-y-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-purple-700 uppercase tracking-wide">Sections</span>
            <span className="text-xs text-purple-600">— larger AI-generated content blocks</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {['103_rejections', 'claims_section', 'preliminary_amendment_remarks', '102_rejections'].map((f) => (
              <span key={f} className="inline-flex items-center px-2 py-0.5 rounded bg-purple-100 text-purple-800 text-xs font-mono">
                {`{{${f}}}`}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="space-y-1.5">
        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Example</p>
        <div className="flex items-start gap-3">
          <div className="flex-1 rounded-md border border-slate-200 bg-white p-2.5 text-xs text-slate-600 font-mono">
            <div className="text-slate-400 text-[10px] mb-1.5">BEFORE (template)</div>
            <div>Re: App. No. <span className="bg-teal-100 text-teal-800 rounded px-0.5">{'{{serial_number}}'}</span></div>
            <div className="mt-0.5">Examiner: <span className="bg-teal-100 text-teal-800 rounded px-0.5">{'{{examiner_name}}'}</span></div>
          </div>
          <div className="flex items-center text-slate-400 mt-5 text-sm">→</div>
          <div className="flex-1 rounded-md border border-slate-200 bg-white p-2.5 text-xs text-slate-600 font-mono">
            <div className="text-slate-400 text-[10px] mb-1.5">AFTER (filled)</div>
            <div>Re: App. No. <span className="text-slate-800">16/123,456</span></div>
            <div className="mt-0.5">Examiner: <span className="text-slate-800">John Smith</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function UploadStep({
  selectedFile,
  onFileChange,
  templateName,
  onTemplateNameChange,
  isAutoDetect,
  onAutoDetectChange,
  fileInputRef,
  disabled,
}: {
  selectedFile: File | null;
  onFileChange: (file: File | null) => void;
  templateName: string;
  onTemplateNameChange: (name: string) => void;
  isAutoDetect: boolean;
  onAutoDetectChange: (val: boolean) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  disabled: boolean;
}) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">
        Upload your firm's response template as a <strong>.docx</strong> file.
      </p>

      <div className="border-2 border-dashed border-slate-200 rounded-lg p-5 text-center hover:border-slate-300 transition-colors">
        <input
          ref={fileInputRef}
          type="file"
          accept=".docx"
          disabled={disabled}
          className="hidden"
          id="wizard-file-input"
          onChange={(e) => {
            const file = e.target.files?.[0] || null;
            onFileChange(file);
            if (file) onTemplateNameChange(file.name.replace(/\.docx$/i, ''));
          }}
        />
        {selectedFile ? (
          <div className="flex items-center justify-center gap-2 text-sm text-slate-700">
            <FileText className="w-4 h-4 text-teal-600 flex-shrink-0" />
            <span className="truncate max-w-[240px]">{selectedFile.name}</span>
            <button
              type="button"
              className="text-slate-400 hover:text-red-500 flex-shrink-0"
              onClick={() => {
                onFileChange(null);
                onTemplateNameChange('');
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <label htmlFor="wizard-file-input" className="cursor-pointer block">
            <Upload className="w-8 h-8 text-slate-300 mx-auto mb-1.5" />
            <span className="text-sm text-slate-500">Click to select a .docx file</span>
          </label>
        )}
      </div>

      <div className="space-y-1">
        <label className="text-xs font-medium text-slate-600">Template Name</label>
        <input
          type="text"
          value={templateName}
          onChange={(e) => onTemplateNameChange(e.target.value)}
          placeholder="e.g. Non-Final Response — Firm Standard"
          disabled={disabled}
          className="w-full text-sm px-3 py-1.5 rounded-md border border-slate-200 bg-white text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-teal-400 disabled:opacity-50"
        />
      </div>

      <div className="space-y-2">
        <p className="text-xs font-medium text-slate-600">Placeholder detection mode:</p>
        <div className="space-y-2">
          <label
            className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
              isAutoDetect ? 'border-teal-300 bg-teal-50' : 'border-slate-200 hover:bg-slate-50'
            }`}
          >
            <input
              type="radio"
              name="wizard-detect-mode"
              checked={isAutoDetect}
              onChange={() => onAutoDetectChange(true)}
              disabled={disabled}
              className="mt-0.5 flex-shrink-0"
            />
            <div>
              <div className="text-sm font-medium text-slate-700">
                Auto-detect placeholders{' '}
                <span className="text-xs font-normal text-teal-600">(Recommended)</span>
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                AI reads your document and inserts{' '}
                <code className="bg-slate-100 px-1 rounded">{'{{field}}'}</code> markers automatically.
                Use this when your doc has literal values like "16/123,456" that should be replaced with case data.
              </div>
            </div>
          </label>

          <label
            className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
              !isAutoDetect ? 'border-purple-300 bg-purple-50' : 'border-slate-200 hover:bg-slate-50'
            }`}
          >
            <input
              type="radio"
              name="wizard-detect-mode"
              checked={!isAutoDetect}
              onChange={() => onAutoDetectChange(false)}
              disabled={disabled}
              className="mt-0.5 flex-shrink-0"
            />
            <div>
              <div className="text-sm font-medium text-slate-700">
                My document already has{' '}
                <code className="bg-slate-100 px-1 rounded">{'{{field}}'}</code> placeholders
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                Skip auto-detection. The AI will map your existing placeholders to the right content types.
              </div>
            </div>
          </label>
        </div>
      </div>
    </div>
  );
}

function ProcessingStep({
  status,
  error,
  onRetry,
}: {
  status: string;
  error: string;
  onRetry: () => void;
}) {
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-4">
        <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
          <X className="w-6 h-6 text-red-600" />
        </div>
        <div className="text-center">
          <p className="text-sm font-medium text-slate-700">Processing Failed</p>
          <p className="text-xs text-slate-500 mt-1">{error}</p>
        </div>
        <Button variant="outline" size="sm" onClick={onRetry}>
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-12 gap-4">
      <Loader2 className="w-10 h-10 animate-spin text-teal-600" />
      <p className="text-sm text-slate-600">{status || 'Processing your template…'}</p>
      <p className="text-xs text-slate-400">This may take up to a minute</p>
    </div>
  );
}

function ReviewFieldsStep({
  editedDynamicFields,
  setEditedDynamicFields,
  editedSections,
  setEditedSections,
  updateFieldKey,
  updateFieldProp,
  removeField,
  addField,
}: {
  editedDynamicFields: [string, FieldValue][];
  setEditedDynamicFields: React.Dispatch<React.SetStateAction<[string, FieldValue][]>>;
  editedSections: [string, FieldValue][];
  setEditedSections: React.Dispatch<React.SetStateAction<[string, FieldValue][]>>;
  updateFieldKey: (setter: React.Dispatch<React.SetStateAction<[string, FieldValue][]>>, index: number, newKey: string) => void;
  updateFieldProp: (setter: React.Dispatch<React.SetStateAction<[string, FieldValue][]>>, index: number, prop: 'description' | 'example', newValue: string) => void;
  removeField: (setter: React.Dispatch<React.SetStateAction<[string, FieldValue][]>>, index: number) => void;
  addField: (setter: React.Dispatch<React.SetStateAction<[string, FieldValue][]>>) => void;
}) {
  const hasFields = editedDynamicFields.length > 0 || editedSections.length > 0;

  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600">
        <strong>Dynamic Fields</strong> are short auto-filled values (names, dates, serial numbers).{' '}
        <strong>Sections</strong> are larger AI-generated content blocks (remarks, rejection responses).
      </div>

      {!hasFields ? (
        <div className="rounded-lg border border-dashed border-slate-200 p-6 text-center space-y-3">
          <p className="text-sm text-slate-500">No fields were detected in this template.</p>
          <p className="text-xs text-slate-400">
            You can add fields manually below, or click Next to finish without fields.
          </p>
          <div className="flex justify-center gap-4">
            <button
              type="button"
              className="flex items-center gap-1 text-xs text-teal-600 hover:text-teal-800"
              onClick={() => addField(setEditedDynamicFields)}
            >
              <Plus className="w-3 h-3" /> Add Dynamic Field
            </button>
            <button
              type="button"
              className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800"
              onClick={() => addField(setEditedSections)}
            >
              <Plus className="w-3 h-3" /> Add Section
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Dynamic Fields */}
          <div>
            <div className="text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-teal-500 inline-block flex-shrink-0" />
              Dynamic Fields
            </div>
            <div className="space-y-1.5">
              {editedDynamicFields.map(([key, fieldVal], idx) => (
                <div key={idx} className="flex gap-1">
                  <div className="flex-[2] flex items-start min-w-0">
                    <div className="flex items-center bg-white rounded border border-slate-200 focus-within:ring-1 focus-within:ring-teal-400 w-full">
                      <span className="text-xs font-medium text-slate-400 pl-1.5 select-none">{'{{'}</span>
                      <input
                        type="text"
                        className="flex-1 text-xs px-0.5 py-1 font-medium text-teal-700 focus:outline-none bg-transparent min-w-0"
                        value={stripBraces(key)}
                        placeholder="field_name"
                        onChange={(e) => updateFieldKey(setEditedDynamicFields, idx, e.target.value)}
                      />
                      <span className="text-xs font-medium text-slate-400 pr-1.5 select-none">{'}}'}</span>
                    </div>
                  </div>
                  <div className="flex-[3] flex flex-col gap-0.5 min-w-0">
                    <input
                      type="text"
                      className="text-xs bg-white rounded px-2 py-1 border border-slate-200 text-slate-600 focus:outline-none focus:ring-1 focus:ring-teal-400 min-w-0"
                      value={fieldVal.description}
                      placeholder="Description"
                      onChange={(e) => updateFieldProp(setEditedDynamicFields, idx, 'description', e.target.value)}
                    />
                    <input
                      type="text"
                      className="text-xs bg-white rounded px-2 py-1 border border-slate-200 text-slate-400 italic focus:outline-none focus:ring-1 focus:ring-teal-400 min-w-0"
                      value={fieldVal.example}
                      placeholder="Example value"
                      onChange={(e) => updateFieldProp(setEditedDynamicFields, idx, 'example', e.target.value)}
                    />
                  </div>
                  <button
                    type="button"
                    className="p-0.5 text-slate-400 hover:text-red-500 flex-shrink-0 mt-1"
                    onClick={() => removeField(setEditedDynamicFields, idx)}
                    title="Remove field"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              className="mt-1.5 flex items-center gap-1 text-xs text-teal-600 hover:text-teal-800"
              onClick={() => addField(setEditedDynamicFields)}
            >
              <Plus className="w-3 h-3" /> Add Field
            </button>
          </div>

          {/* Sections */}
          <div>
            <div className="text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-purple-500 inline-block flex-shrink-0" />
              Sections
            </div>
            <div className="space-y-1.5">
              {editedSections.map(([key, fieldVal], idx) => (
                <div key={idx} className="flex gap-1">
                  <div className="flex-[2] flex items-start min-w-0">
                    <div className="flex items-center bg-white rounded border border-slate-200 focus-within:ring-1 focus-within:ring-purple-400 w-full">
                      <span className="text-xs font-medium text-slate-400 pl-1.5 select-none">{'{{'}</span>
                      <input
                        type="text"
                        className="flex-1 text-xs px-0.5 py-1 font-medium text-purple-700 focus:outline-none bg-transparent min-w-0"
                        value={stripBraces(key)}
                        placeholder="section_name"
                        onChange={(e) => updateFieldKey(setEditedSections, idx, e.target.value)}
                      />
                      <span className="text-xs font-medium text-slate-400 pr-1.5 select-none">{'}}'}</span>
                    </div>
                  </div>
                  <div className="flex-[3] flex flex-col gap-0.5 min-w-0">
                    <input
                      type="text"
                      className="text-xs bg-white rounded px-2 py-1 border border-slate-200 text-slate-600 focus:outline-none focus:ring-1 focus:ring-purple-400 min-w-0"
                      value={fieldVal.description}
                      placeholder="Description"
                      onChange={(e) => updateFieldProp(setEditedSections, idx, 'description', e.target.value)}
                    />
                    <textarea
                      className="text-xs bg-white rounded px-2 py-1 border border-slate-200 text-slate-400 italic focus:outline-none focus:ring-1 focus:ring-purple-400 min-w-0 resize-y"
                      rows={1}
                      value={fieldVal.example}
                      placeholder="Example content"
                      onChange={(e) => updateFieldProp(setEditedSections, idx, 'example', e.target.value)}
                    />
                  </div>
                  <button
                    type="button"
                    className="p-0.5 text-slate-400 hover:text-red-500 flex-shrink-0 mt-1"
                    onClick={() => removeField(setEditedSections, idx)}
                    title="Remove section"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              className="mt-1.5 flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800"
              onClick={() => addField(setEditedSections)}
            >
              <Plus className="w-3 h-3" /> Add Section
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function ResponseTypeStep({
  responseType,
  onResponseTypeChange,
}: {
  responseType: string;
  onResponseTypeChange: (val: string) => void;
}) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">
        Tell us what type of USPTO response this template is for. This guides the AI when generating content.
      </p>

      <div className="space-y-1.5">
        <label className="text-xs font-medium text-slate-600">Response Type</label>
        <Select
          value={responseType || 'none'}
          onValueChange={(val) => onResponseTypeChange(val === 'none' ? '' : val)}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select a response type…" />
          </SelectTrigger>
          <SelectContent className="max-h-[min(60vh,320px)] overflow-y-auto">
            <SelectItem value="none">
              <span className="text-slate-400">None</span>
            </SelectItem>
            {RESPONSE_TYPE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                <div>
                  <div className="font-medium text-sm">{opt.label}</div>
                  <div className="text-xs text-slate-400">{opt.description}</div>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <p className="text-xs text-slate-400">
        You can skip this step or change the response type later in the template editor.
      </p>
    </div>
  );
}

function DoneStep({
  newTemplate,
  onViewTemplate,
  onDone,
}: {
  newTemplate: TemplateItem | null;
  onViewTemplate: () => void;
  onDone: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-8 gap-4 text-center">
      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
        <CheckCircle2 className="w-8 h-8 text-green-600" />
      </div>
      <div>
        <h3 className="text-lg font-semibold text-slate-800">Template Ready!</h3>
        <p className="text-sm text-slate-500 mt-1">Your template has been added and is ready to use.</p>
        {newTemplate && (
          <p className="text-xs text-slate-400 mt-1">{newTemplate.template_name.split('/').pop()}</p>
        )}
      </div>
      <div className="flex gap-3 mt-2">
        <Button variant="outline" onClick={onDone}>
          Done
        </Button>
        <Button className="bg-teal-600 hover:bg-teal-700" onClick={onViewTemplate}>
          View Template
        </Button>
      </div>
    </div>
  );
}

// --- Main wizard ---

export interface TemplateOnboardingWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fetchTemplates: () => Promise<void>;
  onSelectTemplate: (template: TemplateItem | null) => void;
  existingTemplateIds: Set<string>;
}

export function TemplateOnboardingWizard({
  open,
  onOpenChange,
  fetchTemplates,
  onSelectTemplate,
  existingTemplateIds,
}: TemplateOnboardingWizardProps) {
  const [currentStep, setCurrentStep] = useState<WizardStep>('intro');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [templateName, setTemplateName] = useState('');
  const [isAutoDetect, setIsAutoDetect] = useState(true);
  const [uploadJobId, setUploadJobId] = useState<string | null>(null);
  const [processingStatus, setProcessingStatus] = useState('');
  const [processingError, setProcessingError] = useState('');
  const [newTemplate, setNewTemplate] = useState<TemplateItem | null>(null);
  const [editedDynamicFields, setEditedDynamicFields] = useState<[string, FieldValue][]>([]);
  const [editedSections, setEditedSections] = useState<[string, FieldValue][]>([]);
  const [fieldsDirty, setFieldsDirty] = useState(false);
  const [responseType, setResponseType] = useState('');
  const [isSavingFields, setIsSavingFields] = useState(false);
  const [isSavingType, setIsSavingType] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setCurrentStep('intro');
      setSelectedFile(null);
      setTemplateName('');
      setIsAutoDetect(true);
      setUploadJobId(null);
      setProcessingStatus('');
      setProcessingError('');
      setNewTemplate(null);
      setEditedDynamicFields([]);
      setEditedSections([]);
      setFieldsDirty(false);
      setResponseType('');
      setIsSavingFields(false);
      setIsSavingType(false);
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [open]);

  // Start polling when we enter the processing step
  useEffect(() => {
    if (currentStep !== 'processing' || !uploadJobId) return;

    setProcessingError('');
    setProcessingStatus('Analyzing document…');

    taskApi.startGraduatedPolling(
      async () => {
        const result = await documentApi.getJobStatus(uploadJobId);
        if (result?.status?.toLowerCase().startsWith('complete')) return result;
        if (result?.status?.toLowerCase().includes('error')) return result;
        return null;
      },
      async (result) => {
        if (result?.status?.toLowerCase().startsWith('complete')) {
          try {
            const allTemplates = await taskApi.getAllTemplates();
            const templates = Array.isArray(allTemplates) ? allTemplates : [];
            const newT = templates.find((t: TemplateItem) => !existingTemplateIds.has(t.template_id)) || null;
            if (newT) {
              setNewTemplate(newT);
              const mapping = newT.mapping;
              setEditedDynamicFields(
                mapping?.DynamicFields
                  ? Object.entries(mapping.DynamicFields).map(([k, v]) => [k, normalizeFieldValue(v)] as [string, FieldValue])
                  : []
              );
              setEditedSections(
                mapping?.Sections
                  ? Object.entries(mapping.Sections).map(([k, v]) => [k, normalizeFieldValue(v)] as [string, FieldValue])
                  : []
              );
            }
            setCurrentStep('review-fields');
          } catch {
            setProcessingError('Failed to retrieve template after processing.');
          }
        } else {
          setProcessingError(`Template processing failed: ${result?.status || 'Unknown error'}`);
        }
      },
      (error) => {
        console.error('Processing poll error:', error);
        setProcessingError('Template processing failed.');
      },
      (_attempts: number, elapsedTime: number) => {
        setProcessingStatus(`Analyzing document… (${Math.floor(elapsedTime / 1000)}s)`);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, uploadJobId]);

  const currentStepIndex = STEPS.indexOf(currentStep);
  const progress = ((currentStepIndex + 1) / STEPS.length) * 100;

  // --- Field editing helpers ---
  const updateFieldKey = (
    setter: React.Dispatch<React.SetStateAction<[string, FieldValue][]>>,
    index: number,
    newKey: string,
  ) => {
    setter((prev) => {
      const updated = [...prev];
      updated[index] = [newKey, updated[index][1]];
      return updated;
    });
    setFieldsDirty(true);
  };

  const updateFieldProp = (
    setter: React.Dispatch<React.SetStateAction<[string, FieldValue][]>>,
    index: number,
    prop: 'description' | 'example',
    newValue: string,
  ) => {
    setter((prev) => {
      const updated = [...prev];
      const fieldValue = { ...updated[index][1], [prop]: newValue };
      updated[index] = [updated[index][0], fieldValue];
      return updated;
    });
    setFieldsDirty(true);
  };

  const removeField = (
    setter: React.Dispatch<React.SetStateAction<[string, FieldValue][]>>,
    index: number,
  ) => {
    setter((prev) => prev.filter((_, i) => i !== index));
    setFieldsDirty(true);
  };

  const addField = (
    setter: React.Dispatch<React.SetStateAction<[string, FieldValue][]>>,
  ) => {
    setter((prev) => [...prev, ['', { description: '', example: '' }]]);
    setFieldsDirty(true);
  };

  // --- Step transition handlers ---
  const handleUploadAndProceed = async () => {
    if (!selectedFile) return;
    try {
      setIsUploading(true);
      const response = await taskApi.uploadTemplate(selectedFile, isAutoDetect, templateName.trim() || undefined);
      if (response?.job_id) {
        setUploadJobId(response.job_id);
        setCurrentStep('processing');
      }
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSaveFieldsAndProceed = async () => {
    if (!newTemplate || !fieldsDirty) {
      setCurrentStep('response-type');
      return;
    }
    try {
      setIsSavingFields(true);
      const mapping: { DynamicFields?: Record<string, FieldValue>; Sections?: Record<string, FieldValue> } = {};
      if (editedDynamicFields.length > 0) mapping.DynamicFields = entriesToRecord(editedDynamicFields);
      if (editedSections.length > 0) mapping.Sections = entriesToRecord(editedSections);
      await taskApi.updateTemplateMapping(newTemplate.template_id, mapping);
      setFieldsDirty(false);
    } catch (error) {
      console.error('Save fields error:', error);
    } finally {
      setIsSavingFields(false);
    }
    setCurrentStep('response-type');
  };

  const handleSaveTypeAndProceed = async () => {
    if (newTemplate && responseType) {
      try {
        setIsSavingType(true);
        await taskApi.updateTemplateResponseType(newTemplate.template_id, responseType);
      } catch (error) {
        console.error('Save type error:', error);
      } finally {
        setIsSavingType(false);
      }
    }
    setCurrentStep('done');
  };

  const handleNext = async () => {
    switch (currentStep) {
      case 'upload':
        await handleUploadAndProceed();
        break;
      case 'review-fields':
        await handleSaveFieldsAndProceed();
        break;
      case 'response-type':
        await handleSaveTypeAndProceed();
        break;
      default:
        setCurrentStep((prev) => {
          const idx = STEPS.indexOf(prev);
          return idx < STEPS.length - 1 ? STEPS[idx + 1] : prev;
        });
    }
  };

  const handleBack = () => {
    if (currentStep === 'review-fields') {
      // Skip 'processing' when going back
      setCurrentStep('upload');
    } else {
      setCurrentStep((prev) => {
        const idx = STEPS.indexOf(prev);
        return idx > 0 ? STEPS[idx - 1] : prev;
      });
    }
  };

  const isFirstStep = currentStep === 'intro';
  const showFooterNav = currentStep !== 'processing' && currentStep !== 'done';
  const isNextDisabled =
    (currentStep === 'upload' && !selectedFile) ||
    isUploading ||
    isSavingFields ||
    isSavingType;

  const getNextLabel = () => {
    if (currentStep === 'upload') return isUploading ? 'Uploading…' : 'Upload & Continue';
    if (currentStep === 'review-fields') return isSavingFields ? 'Saving…' : 'Next';
    if (currentStep === 'response-type') return isSavingType ? 'Saving…' : 'Next';
    if (currentStep === 'intro') return "Let's Get Started";
    return 'Continue';
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 'intro':
        return <IntroStep />;
      case 'upload':
        return (
          <UploadStep
            selectedFile={selectedFile}
            onFileChange={setSelectedFile}
            templateName={templateName}
            onTemplateNameChange={setTemplateName}
            isAutoDetect={isAutoDetect}
            onAutoDetectChange={setIsAutoDetect}
            fileInputRef={fileInputRef}
            disabled={isUploading}
          />
        );
      case 'processing':
        return (
          <ProcessingStep
            status={processingStatus}
            error={processingError}
            onRetry={() => {
              setProcessingError('');
              setUploadJobId(null);
              setCurrentStep('upload');
            }}
          />
        );
      case 'review-fields':
        return (
          <ReviewFieldsStep
            editedDynamicFields={editedDynamicFields}
            setEditedDynamicFields={setEditedDynamicFields}
            editedSections={editedSections}
            setEditedSections={setEditedSections}
            updateFieldKey={updateFieldKey}
            updateFieldProp={updateFieldProp}
            removeField={removeField}
            addField={addField}
          />
        );
      case 'response-type':
        return (
          <ResponseTypeStep
            responseType={responseType}
            onResponseTypeChange={setResponseType}
          />
        );
      case 'done':
        return (
          <DoneStep
            newTemplate={newTemplate}
            onViewTemplate={() => {
              onSelectTemplate(newTemplate);
              fetchTemplates();
              onOpenChange(false);
            }}
            onDone={() => {
              fetchTemplates();
              onOpenChange(false);
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[calc(100%-2rem)] sm:max-w-[700px] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
              <Wand2 className="w-4 h-4 text-teal-600" />
            </div>
            <DialogTitle className="text-xl">{STEP_TITLES[currentStep]}</DialogTitle>
          </div>
        </DialogHeader>

        <div className="mb-4">
          <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
            <span>Step {currentStepIndex + 1} of {STEPS.length}</span>
            <span>{Math.round(progress)}% complete</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        <div className="flex-1 overflow-y-auto px-1 min-h-0">
          {renderStepContent()}
        </div>

        {showFooterNav && (
          <DialogFooter className="flex items-center justify-between mt-6 sm:justify-between">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={isFirstStep}
              className="gap-2"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </Button>
            <Button
              onClick={handleNext}
              disabled={isNextDisabled}
              className="gap-2 bg-teal-600 hover:bg-teal-700"
            >
              {(isUploading || isSavingFields || isSavingType) && (
                <Loader2 className="w-4 h-4 animate-spin" />
              )}
              {getNextLabel()}
              {!isUploading && !isSavingFields && !isSavingType && (
                <ChevronRight className="w-4 h-4" />
              )}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
